import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Icon } from "@iconify/react";
import io from "socket.io-client";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";
import "../Accounts/ledger-page.css";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import { generateSettlementWeeks } from "../../utils/helper";
import { useAuth } from "../../context/AuthContext";
import { DAY_MS } from "../../utils/constant";
import SearchableSelect from "../Common/SearchableSelect";
import "./portfolio-position.css";

const SOCKET_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

// const toViewType = (filterType) => {
//   switch (filterType) {
//     case "Client Wise":
//       return "clientWise";
//     case "Outstanding":
//       return "outstanding";
//     case "Script Wise":
//       return "scriptWise";
//     case "All":
//     default:
//       return "all";
//   }
// };

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (v, d = 2) => {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toFixed(d);
};

const mtmColor = (v) => {
  if (v == null) return "";
  return v >= 0 ? "text-success-600" : "text-danger-600";
};

const mtmBg = (v) => {
  if (v == null) return "";
  return v >= 0
    ? "portfolio-mtm-box portfolio-mtm-box--positive"
    : "portfolio-mtm-box portfolio-mtm-box--negative";
};

// ─── Component ──────────────────────────────────────────────────────────────

const PortfolioPositionLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall } = useApi();
  const { profile } = useAuth();

  // Filter state
  const [filterType, setFilterType] = useState("All");
  const [filters, setFilters] = useState({
    userId: "",
    segment: "",
    brokerId: "",
    script: "",
    showClosed: false,
    settlementWeek: "",
  });

  const [scripts, setScripts] = useState([]);

  // Dropdown data
  const [clients, setClients] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [segments, setSegments] = useState([]);

  // Position data
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Live prices from WebSocket (symbol → price)
  const [livePrices, setLivePrices] = useState({});
  const livePricesRef = useRef({});
  // Always holds the latest fetchPositions so socket handlers never go stale
  const fetchPositionsRef = useRef(null);

  // Pagination
  const [limitMode, setLimitMode] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const settlementWeeks = useMemo(() => generateSettlementWeeks(10), []);

  // ── Socket.IO — live price feed ────────────────────────────────────────────
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      reconnection: true,
      transports: ["websocket"],
    });

    socket.on("connect", () => {
      console.log("Portfolio Socket Connected");
    });

    // const isSymbolMatch = (wStr, tick) => {
    //   if (!wStr || !tick) return false;
    //   const parts = wStr.split(" ");
    //   const wName = parts[0];
    //   const wSegment = parts[parts.length - 1];
    //   const wExpiry = parts.length >= 3 ? parts[1] : null;

    //   const tName = (tick.name || tick.s || "").toUpperCase();
    //   let tSeg = tick.exchange || "";
    //   if (tSeg === "NOPT") tSeg = "NFO-OPT";
    //   else if (tSeg === "NFUT") tSeg = "NFO-FUT";
    //   else if (tSeg === "MCX") tSeg = "MCX-FUT";

    //   // 1. Basic Name & Segment Check
    //   if (tName !== wName.toUpperCase()) return false;
    //   if (tSeg && tSeg.toUpperCase() !== wSegment.toUpperCase()) return false;

    //   // 2. Expiry Check
    //   if (wExpiry && wExpiry !== "NO-EXPIRY" && tick.expiry) {
    //     if (tick.expiry !== wExpiry) return false;
    //   }

    //   // 3. Option Specific Check (Strike/Type)
    //   if (wSegment.includes("OPT") && parts.length >= 5) {
    //     const wStrike = parseFloat(parts[2]);
    //     const wOptType = parts[3].toUpperCase();
    //     const tStrike = parseFloat(tick.strike || "");
    //     const tOptType = (tick.type || "").toUpperCase();

    //     if (!isNaN(wStrike) && !isNaN(tStrike) && wStrike !== tStrike)
    //       return false;
    //     if (wOptType && tOptType && wOptType !== tOptType) return false;
    //   }

    //   return true;
    // };

    socket.on("marketDataJson", (obj) => {
      try {
        const ltp = obj.ltp ?? obj.last ?? obj.close ?? null;
        if (ltp === null) return;

        const exactKey = (obj.exactSymbol || obj.symbol || "").toUpperCase();
        const baseName = (obj.name || obj.s || "").toUpperCase();

        setLivePrices((prev) => {
          const next = { ...prev };
          if (exactKey) next[exactKey] = ltp;
          if (baseName) next[baseName] = ltp;
          return next;
        });

        // Store the raw tick object for bid/ask/ltp advanced matching in useMemo
        if (exactKey) livePricesRef.current[exactKey] = obj;
        if (baseName) livePricesRef.current[baseName] = obj;
      } catch (err) { }
    });

    // Auto-refresh positions when any trade is placed or closed (global broadcast)
    // Debounce to avoid hammering the backend on rapid sequential trades
    let refreshTimer = null;
    const scheduleRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        // Use ref so we always call the latest fetchPositions with current filters
        if (fetchPositionsRef.current) fetchPositionsRef.current();
      }, 1500);
    };

    socket.on("tradeUpdate", scheduleRefresh);
    socket.on("accountUpdate", scheduleRefresh);

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      socket.disconnect();
    };
  }, []);

  // Keep the ref pointing at the latest fetchPositions (defined below)
  // This runs after every render so the ref is always fresh
  useEffect(() => {
    fetchPositionsRef.current = fetchPositions;
  });

  // ── Fetch dropdown data ────────────────────────────────────────────────────

  useEffect(() => {
    const fetchDropdownData = async () => {
      const [clientRes, brokerRes, segRes] = await Promise.all([
        AsyncGetApiCall("/users/my-clients"),
        AsyncGetApiCall("/broker"),
        AsyncGetApiCall("/trading-settings/segments/list?status=active"),
      ]);
      if (clientRes?.success) setClients(clientRes.data || []);
      if (brokerRes?.success) setBrokers(brokerRes.data || []);
      if (segRes?.success) setSegments(segRes.data || []);
    };
    fetchDropdownData();
  }, [AsyncGetApiCall]);

  useEffect(() => {
    const fetchScripts = async () => {
      if (!filters.segment) {
        setScripts([]);
        return;
      }
      const res = await AsyncGetApiCall(
        `/trading-settings/scripts?status=active&limitVal=1000&segment=${filters.segment}`,
      );
      if (res?.success) setScripts(res.data || []);
    };
    fetchScripts();
  }, [AsyncGetApiCall, filters.segment]);

  // ── Fetch positions ────────────────────────────────────────────────────────

  const fetchPositions = useCallback(
    async (nextFilters = filters) => {
      setLoading(true);
      try {
        let startDateTime = "";
        let endDateTime = "";
        if (nextFilters.settlementWeek && nextFilters.settlementWeek.includes("|")) {
          const parts = nextFilters.settlementWeek.split("|");
          startDateTime = parts[0].split("T")[0];
          endDateTime = parts[1].split("T")[0];
        }

        const payload = {
          ...(nextFilters.userId && { userId: nextFilters.userId }),
          ...(nextFilters.segment && { segment: nextFilters.segment }),
          ...(nextFilters.brokerId && { brokerId: nextFilters.brokerId }),
          ...(nextFilters.script && { script: nextFilters.script }),
          showClosed: nextFilters.showClosed,
          settlementWeek: nextFilters.settlementWeek,
          startDateTime,
          endDateTime,
          // Note: livePrices NOT sent — MTM is computed client-side on every tick
        };
        const res = await AsyncPostApiCall("/orders/positions", payload);
        if (res?.success) {
          setPositions(res.data || []);
          setSummary(res.summary || null);
        } else {
          toast.error(res?.message || "Failed to fetch positions");
        }
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [filters],
  );

  const isSymbolMatch = (sym, tick) => {
    try {
      if (!sym || !tick) return false;
      const parts = sym.split(" ");
      const wName = parts[0];
      const wSegment = parts[parts.length - 1];
      const wExpiry = parts.length >= 3 ? parts[1] : null;

      const tName = (tick.name || tick.s || "").toUpperCase();
      if (tName !== wName.toUpperCase()) return false;

      let tSeg = tick.exchange || "";
      if (tSeg === "NOPT") tSeg = "NFO-OPT";
      else if (tSeg === "NFUT") tSeg = "NFO-FUT";
      else if (tSeg === "MCX") tSeg = "MCX-FUT";
      if (tSeg && tSeg.toUpperCase() !== wSegment.toUpperCase()) return false;

      if (wExpiry && wExpiry !== "NO-EXPIRY" && tick.expiry) {
        if (tick.expiry !== wExpiry) return false;
      }

      // 3. Option Specific Check (Strike/Type)
      if (wSegment.includes("OPT") && parts.length >= 5) {
        const wStrike = parseFloat(parts[2]);
        const wOptType = parts[3].toUpperCase();

        let tStrike = parseFloat(tick.strike || "");
        let tOptType = (tick.type || "").toUpperCase();

        // If tick missing fields, try to extract from symbol
        if ((isNaN(tStrike) || !tOptType) && tick.symbol) {
          const match = /(\d+)\s?(CE|PE|CA|PA)$/i.exec(tick.symbol);
          if (match) {
            if (isNaN(tStrike)) tStrike = parseFloat(match[1]);
            if (!tOptType) tOptType = match[2].toUpperCase();
          }
        }

        // Now strictly compare
        if (isNaN(tStrike) || tStrike !== wStrike) return false;
        if (!tOptType || tOptType !== wOptType) return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  // Initial load
  useEffect(() => {
    fetchPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Enrich positions with current live price + client-side MTM ───────────
  // Runs on every socket tick without hitting the backend
  const enrichedPositions = useMemo(() => {

    return positions.map((row) => {
      // ── Multi-key tick lookup (priority order) ────────────────────────
      // 1. Direct exact symbol match (e.g. "XRP31DEC25", "NIFTY26MAY24000CE")
      const symUpper = (row.symbol || "").toUpperCase();
      // 2. Script/base name (e.g. "XRP", "NIFTY")
      const scriptUpper = (row.script || row.symbol?.split(" ")[0] || "").toUpperCase();

      let tick =
        livePricesRef.current[symUpper] ||           // exact compact symbol
        livePricesRef.current[row.symbol] ||          // original case
        livePricesRef.current[scriptUpper] ||         // base script name (XRP → XRP)
        livePricesRef.current[row.script] ||          // script field direct
        // 3. Fallback: find by isSymbolMatch (covers spaced symbols like "NIFTY 2026-05-29 24000 CE NFO-OPT")
        Object.values(livePricesRef.current).find((t) => isSymbolMatch(row.symbol, t)) ||
        null;

      const livePrice = tick ? (tick.ltp ?? tick.last ?? tick.close ?? null) : null;
      const currentBid = tick ? (tick.bid || livePrice) : null;
      const currentAsk = tick ? (tick.ask || livePrice) : null;
      let unrealisedMTM = null;

      // Real-time hierarchy sharing for unrealised
      let unrealisedUplineMTM = 0;
      let unrealisedMasterMTM = 0;

      if (livePrice !== null) {
        const openQty = Math.abs(row.netQty);
        if (row.isClosed) {
          unrealisedMTM = 0;
        } else if (row.isShort) {
          unrealisedMTM = (row.sellAvg - (currentAsk ?? livePrice)) * openQty;
        } else {
          unrealisedMTM = ((currentBid ?? livePrice) - row.buyAvg) * openQty;
        }
        unrealisedMTM = +unrealisedMTM.toFixed(2);

        // Calculate hierarchical shares for unrealised part
        // Dabba Logic: Hierarchy takes the opposite side
        const totalHierarchyUnrealised = unrealisedMTM * -1;
        const pct = row.partnershipPct ?? 0;
        unrealisedUplineMTM = (totalHierarchyUnrealised * (100 - pct)) / 100;
        unrealisedMasterMTM = totalHierarchyUnrealised - unrealisedUplineMTM;
      }

      return {
        ...row,
        livePrice,
        currentBid,
        currentAsk,
        unrealisedMTM,
        unrealisedUplineMTM,
        unrealisedMasterMTM,
      };
    });
  }, [positions, livePrices]);

  // Summary totals also recomputed client-side
  const enrichedSummary = useMemo(() => {
    if (!summary) return null;

    const totalUnrealisedMTM = enrichedPositions.reduce((acc, r) => acc + (r.unrealisedMTM ?? 0), 0);
    const totalUnrealisedUplineMTM = enrichedPositions.reduce((acc, r) => acc + (r.unrealisedUplineMTM || 0), 0);
    const totalUnrealisedMasterMTM = enrichedPositions.reduce((acc, r) => acc + (r.unrealisedMasterMTM || 0), 0);

    const isSubAdmin = profile?.role?.toLowerCase().includes("subadmin");

    return {
      ...summary,
      totalUnrealisedMTM: +totalUnrealisedMTM.toFixed(2),
      // Realised + Unrealised combined for hierarchy
      totalDownlineMTM: +(summary.totalDownlineMTM ?? 0).toFixed(2),
      totalUplineMTM: +((summary.totalUplineMTM ?? 0) + totalUnrealisedUplineMTM).toFixed(2),
      totalMasterMTM: +((summary.totalMasterMTM ?? 0) + totalUnrealisedMasterMTM).toFixed(2),
      // "Self MTM" for the logged-in user
      totalSelfMTM: isSubAdmin
        ? +((summary.totalMasterMTM ?? 0) + totalUnrealisedMasterMTM).toFixed(2)
        : +((summary.totalUplineMTM ?? 0) + totalUnrealisedUplineMTM).toFixed(2),
    };
  }, [summary, enrichedPositions, profile]);

  // ── Handle Apply / Clear ─────────────────────────────────────────────────

  const handleApply = () => {
    setCurrentPage(1);
    fetchPositions();
  };

  const handleClear = () => {
    const clearedFilters = {
      userId: "",
      segment: "",
      brokerId: "",
      script: "",
      showClosed: false,
      settlementWeek: "",
    };
    setFilters(clearedFilters);
    setFilterType("All");
    setCurrentPage(1);
    fetchPositions(clearedFilters);
  };

  // ── Pagination ────────────────────────────────────────────────────────────

  const pageSize =
    limitMode === "all"
      ? Math.max(enrichedPositions.length, 1)
      : parseInt(limitMode, 10);
  const totalPages = Math.max(
    1,
    Math.ceil(enrichedPositions.length / pageSize),
  );
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = enrichedPositions.slice(start, start + pageSize);
  const total = enrichedPositions.length;
  const showingFrom = total === 0 ? 0 : start + 1;
  const showingTo = total === 0 ? 0 : Math.min(start + pageRows.length, total);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const pageButtons = useMemo(() => {
    const n = totalPages;
    if (n <= 0) return [];
    const cap = Math.min(n, 5);
    const out = [];
    for (let i = 0; i < cap; i += 1) {
      let pageNum;
      if (n <= 5) pageNum = i + 1;
      else if (safePage <= 3) pageNum = i + 1;
      else if (safePage >= n - 2) pageNum = n - 4 + i;
      else pageNum = safePage - 2 + i;
      out.push(pageNum);
    }
    return out;
  }, [totalPages, safePage]);

  // ── Summary Cards ────────────────────────────────────────────────────────

  const summaryCards = enrichedSummary
    ? [
      {
        title: "C. MTM",
        value:
          enrichedSummary.totalRealisedMTM != null
            ? `${enrichedSummary.totalRealisedMTM >= 0 ? "+" : ""}₹${fmt(enrichedSummary.totalRealisedMTM)}`
            : "—",
        cardClass: "portfolio-card-cmtm",
        valueClass: mtmColor(enrichedSummary.totalRealisedMTM),
      },
      {
        title: "U. MTM",
        value:
          enrichedSummary.totalUnrealisedMTM != null
            ? `${enrichedSummary.totalUnrealisedMTM >= 0 ? "+" : ""}₹${fmt(enrichedSummary.totalUnrealisedMTM)}`
            : "—",
        cardClass: "portfolio-card-umtm",
        valueClass: mtmColor(enrichedSummary.totalUnrealisedMTM),
      },
      {
        title: "D. MTM",
        value:
          enrichedSummary.totalDownlineMTM != null
            ? `${enrichedSummary.totalDownlineMTM >= 0 ? "+" : ""}₹${fmt(enrichedSummary.totalDownlineMTM)}`
            : "₹0.00",
        cardClass: "portfolio-card-dmtm",
        valueClass: mtmColor(enrichedSummary.totalDownlineMTM),
      },
      {
        title: "S. MTM",
        value:
          enrichedSummary.totalSelfMTM != null
            ? `${enrichedSummary.totalSelfMTM >= 0 ? "+" : ""}₹${fmt(enrichedSummary.totalSelfMTM)}`
            : `₹${fmt((enrichedSummary.totalRealisedMTM || 0) + (enrichedSummary.totalUnrealisedMTM || 0))}`,
        cardClass: "portfolio-card-smtm",
        valueClass: mtmColor(enrichedSummary.totalSelfMTM),
      },
      {
        title: "T.BUY",
        value: String(enrichedSummary.totalBuyQty ?? 0),
        cardClass: "portfolio-card-tbuy",
        valueClass: "text-primary-light",
      },
      {
        title: "T.SELL",
        value: String(enrichedSummary.totalSellQty ?? 0),
        cardClass: "portfolio-card-tsell",
        valueClass: "text-primary-light",
      },
      {
        title: "T.NET",
        value: String(enrichedSummary.netQty ?? 0),
        cardClass: "portfolio-card-tnet",
        valueClass: "text-primary-light",
      },
    ]
    : [];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {loading && <Loader />}

      {/* Breadcrumb */}
      <div className="d-flex align-items-center gap-2 text-secondary-light mb-24">
        <Icon icon="tabler:layout-grid" className="icon text-xl" />
        <Icon icon="tabler:chevron-right" className="icon text-sm" />
        <span className="fw-semibold text-primary-light">
          Portfolio / Position
        </span>
      </div>

      {/* ── Filter Card ─────────────────────────────────────────────────────── */}
      <div className="card border shadow-sm radius-16 mb-24 ledger-filter-card portfolio-filter-card">
        <div className="card-body p-24">
          {/* View type radio */}
          {/* <div className="d-flex flex-wrap align-items-start justify-content-between gap-1 gap-lg-4 mb-24 pb-2 portfolio-filter-top">
            {FILTER_OPTIONS.map((option) => (
              <div key={option} className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="radio"
                  name="portfolio_filter_type"
                  id={`portfolio-filter-${option.replace(/\s+/g, "-")}`}
                  checked={filterType === option}
                  onChange={() => setFilterType(option)}
                />
                <label
                  className="form-check-label fw-bold text-primary-light small"
                  htmlFor={`portfolio-filter-${option.replace(/\s+/g, "-")}`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div> */}

          {/* Filter dropdowns + actions in one row */}
          <div className="row g-4 portfolio-filter-row align-items-end">
            {/* Segment */}
            <div className="col-12 col-md-6 col-xl-3 portfolio-filter-col">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Segment
              </label>
              <SearchableSelect
                placeholder="All Segments"
                value={filters.segment}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    segment: e.target.value,
                    script: "",
                  }))
                }
                options={segments.map((s) => ({
                  label: s.displayName || s.name,
                  value: s.name,
                }))}
              />
            </div>

            {/* Broker */}
            <div className="col-12 col-md-6 col-xl-3 portfolio-filter-col">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Broker
              </label>
              <SearchableSelect
                placeholder="All Brokers"
                value={filters.brokerId}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, brokerId: e.target.value }))
                }
                options={brokers.map((b) => ({ label: b.name, value: b._id }))}
              />
            </div>

            {/* Client */}
            <div className="col-12 col-md-6 col-xl-3 portfolio-filter-col">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Client
              </label>
              <SearchableSelect
                placeholder="All Clients"
                value={filters.userId}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, userId: e.target.value }))
                }
                options={clients.map((c) => ({
                  label: `${c.loginId} — ${c.name}`,
                  value: c._id,
                }))}
              />
            </div>

            {/* Script */}
            <div className="col-12 col-md-6 col-xl-3 portfolio-filter-col">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Instrument
              </label>
              <SearchableSelect
                placeholder={
                  filters.segment ? "Select script" : "Select Segment First"
                }
                disabled={!filters.segment}
                value={filters.script}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, script: e.target.value }))
                }
                options={scripts.map((s) => ({
                  label: s.name,
                  value: s.name,
                }))}
              />
            </div>

            {/* Settlement Week */}
            <div className="col-12 col-md-6 col-xl-3 portfolio-filter-col">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Settlement Week
              </label>
              <SearchableSelect
                placeholder="Select Week"
                value={filters.settlementWeek}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, settlementWeek: e.target.value }))
                }
                options={settlementWeeks.map((w) => ({
                  label: w.label,
                  value: w.value,
                }))}
              />
            </div>

            <div className="col-12 col-xl-auto portfolio-filter-col portfolio-filter-actions-col">
              <div className="d-flex flex-wrap align-items-center justify-content-xl-end gap-3 portfolio-filter-actions">
                <button
                  type="button"
                  className="btn btn-sm radius-8 px-20 py-10 btn-ledger-apply"
                  onClick={handleApply}
                  disabled={loading}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="btn btn-sm radius-8 px-20 py-10 btn-ledger-clear"
                  onClick={handleClear}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn-sm radius-8 px-20 py-10 btn-outline-secondary d-flex align-items-center justify-content-center gap-1"
                  onClick={fetchPositions}
                  disabled={loading}
                  title="Refresh positions"
                >
                  <Icon icon="tabler:refresh" className="me-1" />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────────────── */}
      {summaryCards.length > 0 && (
        <div className="card border shadow-sm radius-16 mb-24 p-16 p-md-24">
          <div className="portfolio-summary-grid">
            {summaryCards.map((card) => (
              <div
                key={card.title}
                className={`portfolio-summary-card ${card.cardClass}`}
              >
                <p className="small text-secondary-light mb-1">{card.title}</p>
                <p className={`mb-0 fs-6 fw-semibold ${card.valueClass}`}>
                  {card.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Position Table ────────────────────────────────────────────────── */}
      <div className="card border shadow-sm radius-16 ledger-table-card">
        <div className="card-body p-24 pb-0 position-relative" style={{ zIndex: 10 }}>
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-16">
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary-light">Show</span>
              <SearchableSelect
                placeholder="Show"
                value={limitMode}
                showSearch={false}
                onChange={(e) => {
                  setLimitMode(e.target.value);
                  setCurrentPage(1);
                }}
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive px-16 pb-16">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr className="bg-neutral-100">
                <th className="fw-semibold text-primary-light">Sn.</th>
                <th className="fw-semibold text-primary-light">Client</th>
                <th className="fw-semibold text-primary-light">Instrument</th>
                <th className="fw-semibold text-primary-light">Total Buy</th>
                <th className="fw-semibold text-primary-light">Buy Avg.</th>
                <th className="fw-semibold text-primary-light">Total Sell</th>
                <th className="fw-semibold text-primary-light">Sell Avg.</th>
                <th className="fw-semibold text-primary-light">Net Qty.</th>
                <th className="fw-semibold text-primary-light">LTP</th>
                <th className="fw-semibold text-primary-light">MTM</th>
                <th className="fw-semibold text-primary-light">Expiry</th>
                <th className="fw-semibold text-primary-light text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length > 0 ? (
                pageRows.map((row, index) =>
                  <tr
                    key={`${row.userId?._id || row.userId}::${row.symbol}::${index}`}
                    className={row.isClosed ? "opacity-50" : ""}
                  >
                    <td className="text-secondary-light">
                      {start + index + 1}
                    </td>

                    {/* Client */}
                    <td className="text-secondary-light">
                      <span className="d-block small">
                        {row.userId?.loginId || "—"}
                      </span>
                      <span className="text-primary-light fw-medium">
                        {row.userId?.name || "—"}
                      </span>
                    </td>

                    {/* Instrument */}
                    <td className="text-secondary-light">
                      <span className="d-block small">
                        {row.segment} {row.expiry ? `· ${row.expiry}` : ""}
                      </span>
                      <span className="fw-medium text-primary-light">
                        {["NFO", "MCX", "CDS", "COMEX"].some((s) =>
                          (row.segment || "").toUpperCase().includes(s),
                        )
                          ? row.symbol
                          : row.script || row.symbol}
                      </span>
                      {row.isShort && (
                        <span className="badge bg-danger-focus text-danger-600 ms-1 text-xs">
                          SHORT
                        </span>
                      )}
                      {row.isClosed && (
                        <span className="badge bg-neutral-focus text-secondary-light ms-1 text-xs">
                          FLAT
                        </span>
                      )}
                    </td>

                    {/* Total Buy */}
                    <td className="text-secondary-light">
                      <span className="d-block small">
                        Lot: {fmt(row.buyLot, 4)}
                      </span>
                      <span>{row.buyQty}</span>
                    </td>

                    {/* Buy Avg */}
                    <td className="text-secondary-light">
                      {fmt(row.buyAvg)}
                    </td>

                    {/* Total Sell */}
                    <td className="text-secondary-light">
                      <span className="d-block small">
                        Lot: {fmt(row.sellLot, 4)}
                      </span>
                      <span>{row.sellQty}</span>
                    </td>

                    {/* Sell Avg */}
                    <td className="text-secondary-light">
                      {fmt(row.sellAvg)}
                    </td>

                    {/* Net Qty */}
                    <td className="text-secondary-light">
                      <span className="d-block small">
                        Lot: {fmt(row.netLot, 4)}
                      </span>
                      <span className="fw-semibold">
                        {Math.abs(row.netQty)}
                      </span>
                    </td>

                    {/* LTP */}
                    <td className="text-secondary-light fw-medium">
                      {row.livePrice != null ? (
                        fmt(row.livePrice)
                      ) : (
                        <span className="text-muted text-xs">No feed</span>
                      )}
                    </td>

                    {/* MTM */}
                    <td className="align-middle">
                      <div className={mtmBg(row.unrealisedMTM)}>
                        <div className="portfolio-mtm-row">
                          <span className="text-secondary-light">C:</span>
                          <span
                            className={`fw-medium ${mtmColor(row.realisedMTM)}`}
                          >
                            ₹{fmt(row.realisedMTM)}
                          </span>
                        </div>
                        <div className="portfolio-mtm-row">
                          <span className="text-secondary-light">U:</span>
                          <span
                            className={`fw-medium ${mtmColor(row.unrealisedMTM)}`}
                          >
                            {row.unrealisedMTM != null
                              ? `₹${fmt(row.unrealisedMTM)}`
                              : "—"}
                          </span>
                        </div>
                        <div className="portfolio-mtm-row">
                          <span className="text-secondary-light">Brk:</span>
                          <span className="text-danger-600">
                            -₹{fmt(row.totalBrokerage)}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* SL / Target */}
                    {/* <td className="text-secondary-light">
                      {row.stopLoss != null ? (
                        <span className="d-block small text-danger-600">
                          SL: {fmt(row.stopLoss)}
                        </span>
                      ) : (
                        <span className="d-block small text-muted">SL: —</span>
                      )}
                      {row.targetPrice != null ? (
                        <span className="d-block small text-success-600">
                          TP: {fmt(row.targetPrice)}
                        </span>
                      ) : (
                        <span className="d-block small text-muted">TP: —</span>
                      )}
                    </td>

                    {/* Expiry */}
                    <td className="text-secondary-light">
                      {row.expiry || "—"}
                    </td>

                    {/* Action */}
                    <td className="text-center">
                      {!row.isClosed && (
                        <button
                          type="button"
                          className="portfolio-action-close"
                          aria-label="Close position"
                          title="Square off / Close position"
                          onClick={async () => {
                            const openQty = Math.abs(row.netQty);
                            let suggestedPrice = "";
                            if (row.netQty > 0) {
                              // Long -> close at Bid
                              suggestedPrice = row.currentBid != null
                                ? String(row.currentBid)
                                : (row.livePrice != null ? String(row.livePrice) : String(row.buyAvg));
                            } else if (row.netQty < 0) {
                              // Short -> close at Ask
                              suggestedPrice = row.currentAsk != null
                                ? String(row.currentAsk)
                                : (row.livePrice != null ? String(row.livePrice) : String(row.sellAvg));
                            }

                            const { isConfirmed, value } = await Swal.fire({
                              title: "Close Position",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonColor: "#d33",
                              cancelButtonColor: "#6c757d",
                              confirmButtonText: "Close Position",
                              customClass: { popup: "swal-sm" },
                              html: `
                                <p class="text-sm mb-2">
                                  <strong>${row.symbol}</strong> — ${row.segment}<br/>
                                  <span class="${row.isShort ? "text-danger" : "text-success"}">
                                    ${row.isShort ? "SHORT" : "LONG"} ${openQty} qty
                                  </span>
                                </p>
                                <div class="mb-2">
                                  <label class="form-label text-sm">Close Qty (max: ${openQty})</label>
                                  <input id="swal-close-qty" type="number"
                                    class="swal2-input" style="width:90%"
                                    min="1" max="${openQty}" value="${openQty}" />
                                </div>
                                <div>
                                  <label class="form-label text-sm">Close Price *</label>
                                  <input id="swal-close-price" type="number"
                                    class="swal2-input" style="width:90%"
                                    step="0.01" min="0.01" value="${suggestedPrice}" />
                                </div>`,
                              preConfirm: () => {
                                const qtyEl =
                                  document.getElementById("swal-close-qty");
                                const priceEl =
                                  document.getElementById("swal-close-price");
                                const qty = parseFloat(qtyEl?.value);
                                const price = parseFloat(priceEl?.value);

                                if (!price || price <= 0) {
                                  Swal.showValidationMessage(
                                    "Close price is required and must be > 0",
                                  );
                                  return false;
                                }
                                if (!qty || qty <= 0 || qty > openQty) {
                                  Swal.showValidationMessage(
                                    `Close qty must be between 1 and ${openQty}`,
                                  );
                                  return false;
                                }
                                return { qty, price };
                              },
                            });

                            if (isConfirmed && value) {
                              try {
                                const res = await AsyncPostApiCall(
                                  "/orders/close-position",
                                  {
                                    userId: row.userId?._id || row.userId,
                                    symbol: row.symbol,
                                    script: row.script || row.symbol.split(" ")[0],
                                    segment: row.segment,
                                    exchange: row.exchange,
                                    expiry: row.expiry,
                                    lotSize: row.lotSize,
                                    closeQty: value.qty,
                                    closePrice: value.price,
                                  },
                                );

                                if (res?.success) {
                                  const pnl = res.data?.realisedPnl;
                                  const pnlText =
                                    pnl != null
                                      ? ` | P&L: ${pnl >= 0 ? "+" : ""}₹${pnl.toFixed(2)}`
                                      : "";
                                  toast.success(`${res.message}${pnlText}`);
                                  fetchPositions();
                                } else {
                                  toast.error(
                                    res?.message ||
                                    "Failed to close position",
                                  );
                                }
                              } catch {
                                toast.error("Error closing position");
                              }
                            }
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                )
              ) : (
                <tr>
                  <td
                    colSpan={13}
                    className="text-center py-40 text-secondary-light"
                  >
                    {loading ? "Loading positions…" : "No positions found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-top d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-24">
          <p className="mb-0 text-secondary-light text-sm">
            Showing{" "}
            <span className="fw-medium text-primary-light">
              {showingFrom} to {showingTo} of {total} positions
            </span>
          </p>
          {total > 0 && (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
              >
                <Icon icon="tabler:chevron-left" className="icon" />
              </button>
              {pageButtons.map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  className={`btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center ${pageNum === safePage
                    ? "ledger-pagination-btn active-page-num"
                    : "bg-base text-secondary-light"
                    }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
              {totalPages > 5 && safePage < totalPages - 2 && (
                <>
                  <span className="text-secondary-light px-1">…</span>
                  <button
                    type="button"
                    className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center bg-base text-secondary-light"
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={safePage >= totalPages}
              >
                <Icon icon="tabler:chevron-right" className="icon" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PortfolioPositionLayer;
